import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Eye, 
  Edit2, 
  Download, 
  Printer,
  Calendar,
  Mail,
  User
} from "lucide-react";

interface ReferralViewerModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  referral: {
    id: number;
    number: string;
    date: string;
    type: string;
    status: "Sent" | "Pending" | "Completed" | "Cancelled";
  } | null;
  onEdit?: () => void;
  isDarkMode?: boolean;
}

export function ReferralViewerModal({
  isOpen,
  onOpenChange,
  referral,
  onEdit,
  isDarkMode = false,
}: ReferralViewerModalProps) {
  if (!referral) return null;

  // Mock referral letter content - in a real app this would come from the referral data
  const mockReferralContent = `Dr. Andrew Staton
QUALIFICATION
Invatech Health
442-450 Stapleton Rd, Eastville, Bristol BS5 6NR
+44 (0) 777 649 55 86

${new Date().toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "long", 
  year: "numeric",
})}

Dear Dr. Smith,
${referral.type}
Bristol Medical Centre

RE: David James (DOB: 18/10/1994) - ${referral.type}

I am writing to refer Mr. David James for ${referral.type.toLowerCase()} consultation regarding his ongoing medical condition.

PRESENTING COMPLAINT:
Patient presents with symptoms requiring specialist assessment and management.

PAST MEDICAL HISTORY:
- Hypertension (diagnosed 2020)
- Type 2 Diabetes Mellitus (well controlled)
- No known drug allergies

CURRENT MEDICATIONS:
- Metformin 500mg BD
- Ramipril 5mg OD
- Aspirin 75mg OD

EXAMINATION FINDINGS:
On examination, patient appears well. Vital signs stable. Further specialist assessment required.

CLINICAL IMPRESSION:
I would be grateful for your expert assessment and recommendations for ongoing management.

Thank you for your attention to this referral.

Yours sincerely,

Dr. Andrew Staton
General Practitioner
GMC Number: 7654321`;

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Referral Letter ${referral.number}</title>
          <style>
            body { 
              font-family: 'Times New Roman', serif; 
              font-size: 12pt; 
              line-height: 1.6; 
              margin: 2cm; 
              white-space: pre-wrap;
            }
            .header { 
              margin-bottom: 20px; 
              border-bottom: 2px solid #000; 
              padding-bottom: 20px; 
            }
            @media print {
              body { margin: 1cm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Referral Letter ${referral.number}</h1>
            <p>Date: ${referral.date} | Status: ${referral.status}</p>
            <p>Type: ${referral.type}</p>
          </div>
          ${mockReferralContent.replace(/\n/g, "<br>")}
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleDownload = () => {
    // In a real app, this would download the actual referral letter file
    const blob = new Blob([mockReferralContent], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `referral-letter-${referral.number}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent 
        className={`max-w-6xl max-h-[90vh] overflow-y-auto ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
        }`}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold">
              Referral Letter Details
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="flex items-center gap-1"
                title="Print Referral"
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="flex items-center gap-1"
                title="Download Referral"
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
              {onEdit && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={onEdit}
                  className="flex items-center gap-1"
                  title="Edit Referral"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Referral Information */}
          <div 
            className={`rounded-lg border p-4 ${
              isDarkMode 
                ? "bg-gray-700 border-gray-600" 
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-medium">Referral Number</span>
                </div>
                <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  {referral.number}
                </p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-medium">Date Created</span>
                </div>
                <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  {referral.date}
                </p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">Status</span>
                </div>
                <Badge 
                  variant="secondary" 
                  className={`text-xs ${
                    referral.status === "Completed" 
                      ? "text-green-600" 
                      : referral.status === "Sent"
                      ? "text-blue-600"
                      : referral.status === "Pending"
                      ? "text-orange-600"
                      : "text-red-600"
                  }`}
                >
                  {referral.status}
                </Badge>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-medium">Referral Type</span>
                </div>
                <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  {referral.type}
                </p>
              </div>
            </div>
          </div>

          {/* Referral Letter Content */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Referral Letter Document</h3>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="text-sm text-gray-500">Medical Letter</span>
              </div>
            </div>
            
            <div 
              className={`rounded-lg border overflow-hidden ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <div className="bg-white p-6 min-h-[600px]">
                <div className="whitespace-pre-wrap font-serif text-sm leading-relaxed text-gray-900">
                  {mockReferralContent}
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                This is a generated medical referral letter
              </p>
            </div>
          </div>

          {/* Additional Actions */}
          <div 
            className={`rounded-lg border p-4 ${
              isDarkMode 
                ? "bg-gray-700 border-gray-600" 
                : "bg-blue-50 border-blue-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium">Distribution</span>
            </div>
            <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              This referral letter has been sent to the appropriate specialist department.
              {referral.status === "Completed" && " Response received and filed in patient record."}
              {referral.status === "Pending" && " Awaiting response from specialist department."}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
